// Decimal fields (amount_at_risk, amounts, ledger totals) are serialized as
// strings by the backend — parse with Number() before formatting/charting.

export type RiskWindow = '24h' | '7d' | '30d';
export type Severity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
export type RiskEntity = 'USER' | 'TRANSACTION' | 'CARD' | 'RECIPIENT' | 'LEDGER';

export const SEVERITY_OPTIONS: Severity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

export const RISK_RULES: Record<string, string> = {
  VELOCITY_BURST: 'Rapid-fire purchases',
  DECLINE_STREAK: 'Repeated card declines',
  SHARED_CARD: 'Same card on multiple accounts',
  SHARED_EMAIL: 'Same email on multiple accounts',
  RECIPIENT_FAN_IN: 'Many accounts paying one recipient',
  AMOUNT_OUTLIER: 'Unusually large purchase',
  NEW_ACCOUNT_HIGH_SPEND: 'High spend on new account',
  PIN_ATTACK: 'PIN brute-force / lockouts',
  EJOR_RING: 'Unusual Ejor network',
  LEDGER_IMBALANCE: 'Ledger debits ≠ credits',
  SUCCESS_WITHOUT_PROVIDER_REF: 'Successful with no provider ref',
  NEGATIVE_MARGIN: 'Sold below cost',
  REFUND_OWED: 'Refund stuck',
  STALE_UNRESOLVED: 'Outcome unresolved too long',
  LATE_REFUND: 'Late charge refunded',
};

export type AlertStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED' | 'DISMISSED' | 'CLEARED';
export type AlertStatusFilter = 'ACTIVE' | 'ALL' | AlertStatus;
export type ReviewAction = 'acknowledge' | 'resolve' | 'dismiss' | 'reopen';

export const ALERT_STATUS_FILTERS: { value: AlertStatusFilter; label: string }[] = [
  { value: 'ACTIVE', label: 'Needs attention' },
  { value: 'OPEN', label: 'Open' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'DISMISSED', label: 'Dismissed (false positive)' },
  { value: 'CLEARED', label: 'Cleared automatically' },
  { value: 'ALL', label: 'All' },
];

// Which review actions each status allows (mirrors the backend's
// VALID_ALERT_TRANSITIONS).
export const ALERT_ACTIONS: Record<AlertStatus, ReviewAction[]> = {
  OPEN: ['acknowledge', 'resolve', 'dismiss'],
  ACKNOWLEDGED: ['resolve', 'dismiss', 'reopen'],
  RESOLVED: ['reopen'],
  DISMISSED: ['reopen'],
  CLEARED: ['resolve', 'reopen'],
};

export const isActiveStatus = (status: string) => status === 'OPEN' || status === 'ACKNOWLEDGED';

export interface RiskAlert {
  id: string;
  alert_key: string;
  rule: string;
  severity: Severity;
  status: AlertStatus;
  title: string;
  detail: string;
  entity_type: RiskEntity;
  entity_ref: string;
  user_id: string | null;
  chat_id: string | null;
  transaction_reference: string | null;
  amount_at_risk: string;
  evidence: Record<string, unknown> | null;
  // When the problem began (e.g. the burst), vs. last seen by a scan.
  first_detected_at: string;
  last_detected_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  cleared_at: string | null;
}

export interface RiskAlertDetail extends RiskAlert {
  history: {
    event_type: string;
    previous_status: string | null;
    new_status: string | null;
    note: string | null;
    actor: string | null;
    created_at: string;
  }[];
}

export interface RuleCount {
  rule: string;
  title: string;
  active: number;
  raised: number;
  resolved: number;
  dismissed: number;
  // dismissed / (resolved + dismissed), null until something was reviewed.
  false_positive_rate: number | null;
}

export interface RiskSummary {
  window: RiskWindow;
  generated_at: string;
  active_alerts: number;
  by_severity: Record<Severity, number>;
  by_rule: RuleCount[];
  amount_at_risk: string;
  flagged_customers: number;
  last_scan_at: string | null;
  last_scan_ms: number | null;
}

// Decimal thresholds arrive as strings, like every other Decimal.
export interface RiskThresholds {
  velocity_window_minutes: number;
  velocity_min_attempts: number;
  decline_streak_high: number;
  decline_streak_critical: number;
  fan_in_min_users: number;
  outlier_min_history: number;
  outlier_multiple: string | number;
  outlier_min_amount: string | number;
  new_account_spend: string | number;
  pin_lockouts_high: number;
  ejor_ring_min: number;
  stale_unresolved_minutes: number;
  refund_pending_minutes: number;
}

export const THRESHOLD_FIELDS: { key: keyof RiskThresholds; label: string; hint: string }[] = [
  { key: 'velocity_min_attempts', label: 'Rapid-fire: attempts', hint: 'Purchases that count as a burst' },
  { key: 'velocity_window_minutes', label: 'Rapid-fire: within (min)', hint: 'Burst time window' },
  { key: 'decline_streak_high', label: 'Declines: high at', hint: 'Declines in 24h → high' },
  { key: 'decline_streak_critical', label: 'Declines: critical at', hint: 'Must be above "high"' },
  { key: 'fan_in_min_users', label: 'Recipient fan-in: accounts', hint: 'Accounts paying one recipient' },
  { key: 'outlier_multiple', label: 'Outlier: × usual', hint: 'Times the customer’s median' },
  { key: 'outlier_min_amount', label: 'Outlier: minimum ₦', hint: 'Ignore purchases below this' },
  { key: 'outlier_min_history', label: 'Outlier: history needed', hint: 'Prior purchases before judging' },
  { key: 'new_account_spend', label: 'New account: spend ₦', hint: 'In the account’s first 24h' },
  { key: 'pin_lockouts_high', label: 'PIN: lockouts', hint: 'Lockouts that raise an alert' },
  { key: 'ejor_ring_min', label: 'Ejor network: people', hint: 'Distinct counterparties' },
  { key: 'stale_unresolved_minutes', label: 'Unresolved after (min)', hint: 'Outcome still unknown' },
  { key: 'refund_pending_minutes', label: 'Refund stuck after (min)', hint: 'In REVERSAL_PENDING' },
];

export interface RiskSettings {
  thresholds: RiskThresholds;
  defaults: RiskThresholds;
  disabled_rules: string[];
  rules: { rule: string; title: string; self_clearing: boolean }[];
  updated_by: string | null;
  updated_at: string | null;
  last_scan_at: string | null;
  last_scan_ms: number | null;
  last_scan_detected: number | null;
}

export interface RiskScanResult {
  detected: number;
  created: number;
  updated: number;
  reopened: number;
  cleared: number;
  duration_ms: number;
}

export interface LinkedAccount {
  user_id: string;
  chat_id: string;
  display_name: string | null;
  link: string;
}

export interface CustomerTransaction {
  reference: string;
  type: string;
  status: string;
  amount: string;
  provider: string;
  recipient: string | null;
  decline_category: string | null;
  created_at: string;
}

export interface CustomerProfile {
  user_id: string;
  chat_id: string;
  display_name: string | null;
  email: string | null;
  is_active: boolean;
  created_at: string;
  onboarding_completed_at: string | null;
  pin_failed_attempts: number;
  pin_lockout_count: number;
  pin_locked_until: string | null;
  risk_score: number;
  card: {
    bank: string;
    card_type: string;
    last4: string;
    email: string;
    added_at: string;
    // Paystack's card signature is stored → exact shared-card matching.
    fingerprint_captured: boolean;
  } | null;
  stats: {
    total_transactions: number;
    successful: number;
    failed: number;
    declined: number;
    unresolved: number;
    total_spent: string;
    average_amount: string;
    largest_amount: string;
    first_transaction_at: string | null;
    last_transaction_at: string | null;
    distinct_recipients: number;
    ejors_initiated: number;
    ejors_paid: number;
  };
  alerts: RiskAlert[];
  linked_accounts: LinkedAccount[];
  recent_transactions: CustomerTransaction[];
  recent_events: { event_type: string; actor: string | null; created_at: string }[];
}

export type ProviderStatus = 'HEALTHY' | 'DEGRADED' | 'DOWN';

export interface ProviderHealth {
  provider: string;
  attempts: number;
  successful: number;
  failed: number;
  unresolved: number;
  success_rate: number;
  failures_last_hour: number;
  status: ProviderStatus;
}

export interface ReconciliationHealth {
  generated_at: string;
  unresolved_count: number;
  unresolved_amount: string;
  oldest_unresolved_minutes: number | null;
  escalated_count: number;
  reversal_pending_count: number;
  reversal_pending_amount: string;
  by_stage: Record<string, number>;
  age_buckets: { label: string; count: number; amount: string }[];
  auto_resolved_24h: number;
  median_resolution_minutes: number | null;
  late_refunds_24h: number;
  ledger_imbalanced_transactions: number;
  ledger_total_debits: string;
  ledger_total_credits: string;
  providers: ProviderHealth[];
  // null = Redis unreachable, so the worker's state is unknown.
  worker_alive: boolean | null;
}

export interface ReconcileRunResult {
  checked: number;
  resolved: number;
  escalated: number;
  // Old purchases skipped by back-off (only on the automatic run).
  deferred: number;
}

export interface ReconcileOneResult {
  reference: string;
  resolved: boolean;
  status: string;
}

export const formatMinutes = (minutes: number | null | undefined) => {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 48) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${Math.floor(minutes / (60 * 24))}d`;
};
